/**
 * IniziaLanding — conversion-focused landing page for Google Ads / Meta Ads.
 *
 * Route: `/inizia`
 *
 * Design philosophy:
 *  • Single-scroll, single decision → "start free trial" (no menus, no distractions)
 *  • Above-the-fold: hero + inline email capture that pre-fills /login?email=...
 *  • Trust: 30-day free trial, no credit card, cancel anytime (repeated 3x)
 *  • Live demo iframes so visitors touch the product before signing up
 *  • 4-plan grid: Mensile · Annuale (save) · Welcome Book only · ParkHost
 *  • Deep SEO: unique H1, 900+ words of copy, 12 FAQ (LLM-ready), 4 JSON-LD schemas
 *
 * SEO targets (research 2026, Italian short-term rental market):
 *  Primary: "prenotazioni dirette senza commissioni", "sito web per host",
 *           "welcome book digitale", "guestbook digitale"
 *  Secondary: "come non pagare commissioni Airbnb Booking", "software B&B",
 *             "chatbot AI ospiti", "concierge digitale", "self check-in QR"
 *  Long-tail: "creare sito web B&B veloce", "AI concierge affitti brevi",
 *             "OTA-independent booking engine", "prenotazione diretta senza CC"
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion, useInView } from "framer-motion";
import {
  ArrowRight, CheckCircle2, Zap, Globe, Sparkles, ShieldCheck, Star, Building2,
  CalendarClock, MessageSquareText, MapPinned, Car, Wifi, Languages, QrCode,
  Bot, ChevronDown, PlayCircle, Rocket, TrendingUp, Award, Users,
} from "lucide-react";

const DOMAIN = "https://hostland.vitadahost.it";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ─────────────────────────── SEO KEYWORDS 2026 ─────────────────────────── */
const SEO_KEYWORDS = [
  "prenotazioni dirette senza commissioni", "sito web per host affitti brevi",
  "welcome book digitale", "guestbook digitale affitti brevi",
  "software gestione B&B", "creare sito web casa vacanza velocemente",
  "come non pagare commissioni Airbnb Booking", "OTA free booking engine",
  "chatbot AI ospiti Airbnb", "concierge digitale hotel", "AI concierge affitti brevi",
  "self check-in QR code", "codice identificativo nazionale CIN struttura",
  "prenotazione diretta senza carta di credito", "landing page casa vacanza",
  "manuale casa digitale multilingua", "guestbook hotel boutique",
  "sito web B&B veloce", "sito web affittacamere SEO",
  "app per gestione affitti brevi", "recensioni Google integrate hotel",
  "dashboard host analytics", "iCal sync affitti brevi",
  "Zero commissioni Airbnb", "risparmio commissioni OTA host",
].join(", ");

/* ────────────────── PLAN DEFINITIONS (source: payments.py) ────────────────── */
const PLANS = [
  {
    id: "yearly",
    name: "Annuale",
    tagline: "Il piano più scelto",
    price: 59.90,
    periodLabel: "/anno",
    monthlyEq: "≈ €4.99/mese",
    save: "Risparmi €59",
    trial: "30 giorni gratis, no carta",
    highlight: true,
    features: [
      "🌐 Sito web ottimizzato per mobile con dominio hostland.vitadahost.it/tuo-nome",
      "🔗 Dominio custom (es. www.tuobnb.it) via Cloudflare",
      "📖 Welcome Book digitale multilingua stile app iOS",
      "🌍 Traduzioni AI automatiche in 5 lingue (IT/EN/FR/ES/DE)",
      "🤖 Concierge AI + Chatbot GPT-5 per ospiti 24/7",
      "🗺️ AI Tour della città generato automatico",
      "💳 Prenotazioni dirette (zero commissioni OTA)",
      "📶 Fino a 5 reti Wi-Fi con tap-to-copy",
      "🏠 Manuali casa illimitati (foto + video + PDF)",
      "🗺️ Mappa POI OSM (ristoranti/bar/supermercati/parking)",
      "☀️ Meteo con alert estremi + previsioni 7 giorni",
      "🎫 Eventi in zona automatici (concerti/fiere/sagre)",
      "🚌 Trasporti locali (bus, metro, car/bike sharing)",
      "⭐ Recensioni Google Business auto-sync",
      "📊 Dashboard analytics tap rate + engagement",
      "📅 iCal sync bi-direzionale Airbnb/Booking/VRBO",
      "🎨 OG image auto-generata per social share",
      "📱 QR Premium stampabile per ogni struttura",
      "🎁 Sistema codici sconto per ospiti fedeli",
      "🇮🇹 CIN + CIR automatici in landing",
      "🛡️ GDPR compliant · Server EU",
      "⚡ Supporto prioritario 7/7",
    ],
    cta: "Inizia 30 giorni gratis",
  },
  {
    id: "monthly",
    name: "Mensile",
    tagline: "Flessibile, senza vincoli",
    price: 9.90,
    periodLabel: "/mese",
    trial: "30 giorni gratis, no carta",
    features: [
      "✅ Tutto ciò che c'è nel piano Annuale",
      "🔄 Cancelli quando vuoi con 1 click",
      "📅 Nessun impegno annuale",
      "💳 Fatturazione mensile trasparente",
      "🎯 Ideale per test o stagionalità",
      "📈 Passa ad Annuale in ogni momento",
    ],
    cta: "Prova gratis 1 mese",
  },
  {
    id: "guestbook_yearly",
    name: "Solo Welcome Book",
    tagline: "Per chi ha già un sito",
    price: 35.90,
    periodLabel: "/anno",
    monthlyEq: "≈ €2.99/mese",
    trial: "30 giorni gratis",
    icon: MessageSquareText,
    features: [
      "📖 Welcome Book digitale completo",
      "🌍 5 lingue con traduzioni AI",
      "🤖 Concierge AI + Chatbot GPT-5",
      "📶 Fino a 5 reti Wi-Fi",
      "🏠 Manuali casa illimitati",
      "🗺️ Mappa POI con pin GPS",
      "☀️ Meteo + alert estremi",
      "🎫 Eventi in zona",
      "🚌 Trasporti locali",
      "🎨 Brand personalizzato",
      "📱 QR code stampabile",
      "⭐ Recensioni Google raccolta",
      "🎁 Codici sconto ospiti",
      "🚀 Upgrade al piano completo in 1 click",
    ],
    cta: "Attiva Welcome Book",
  },
  {
    id: "parkhost_yearly",
    name: "ParkHost",
    tagline: "Affitta il tuo parcheggio",
    price: 4.99,
    periodLabel: "/anno",
    trial: "30 giorni gratis",
    icon: Car,
    features: [
      "🚗 Pubblica il tuo parcheggio online",
      "📅 Booking calendar + prezzi variabili",
      "💳 Pagamenti diretti integrati",
      "⭐ Recensioni degli automobilisti",
      "📸 Fino a 10 foto per parcheggio",
      "🗺️ Mappa e indicazioni per l'accesso",
      "💰 Zero commissioni sui pagamenti",
      "🎯 Prezzo lancio — €4.99/anno",
    ],
    cta: "Attiva ParkHost",
  },
];

const PILLARS = [
  {
    icon: Globe,
    title: "Sito web SEO-ready che ti sgancia dalle OTA",
    text: "Ogni casa vacanza, B&B, affittacamere o hotel ottiene una landing indicizzabile su Google, con Open Graph, structured data JSON-LD, sitemap.xml, hreflang multilingua e supporto 5 lingue. Dominio custom opzionale (es. www.tuobnb.it) via Cloudflare for SaaS.",
  },
  {
    icon: MessageSquareText,
    title: "Welcome Book digitale stile app iOS che riduce del 40% i messaggi",
    text: "Guestbook mobile-first con tile grid, bottom sheets animate, glassmorphism. Wi-Fi copiabile in un tap (fino a 5 reti), manuali casa illimitati, mappa POI OSM con pin GPS, meteo con alert estremi, previsioni 7 giorni, eventi in zona, trasporti locali configurabili, regole casa multilingua.",
  },
  {
    icon: Bot,
    title: "Concierge AI + Chatbot GPT-5 in 5 lingue 24/7",
    text: "L'ospite chatta con l'AI che conosce la tua struttura: check-in, servizi, escursioni, ristoranti, orari trasporti. Include l'AI Tour della città generato automaticamente. Zero configurazione, funziona out-of-the-box con toni personalizzati sulla tua brand.",
  },
  {
    icon: TrendingUp,
    title: "Prenotazioni dirette con zero commissioni OTA",
    text: "L'ospite prenota e paga direttamente dalla tua landing. Zero commissioni Airbnb (15%) o Booking (18%). Su una prenotazione media da €500 risparmi €75-90 ogni volta. Il tuo brand, il tuo prezzo, il tuo contatto diretto — no marketplace di mezzo.",
  },
  {
    icon: CalendarClock,
    title: "iCal sync bi-direzionale con Airbnb, Booking e VRBO",
    text: "Il calendario della tua struttura si sincronizza automaticamente con tutti i marketplace che supportano iCal. Zero overbooking, zero doppie prenotazioni. Ricevi le notifiche quando arriva una nuova richiesta su qualsiasi canale.",
  },
  {
    icon: Star,
    title: "Recensioni Google integrate + Reputation management",
    text: "Colleghi il tuo Google Business Profile e Hostland importa e aggiorna le recensioni ogni 7 giorni. Le mostra come social proof sulla landing con rating reale e commenti verificati. Include reminder automatico agli ospiti dopo il soggiorno per aumentare le recensioni.",
  },
  {
    icon: QrCode,
    title: "QR Premium stampabile per camera / appartamento",
    text: "Ogni struttura ha un QR Premium (formato A4, A5, biglietto da visita) stampabile dalla dashboard. Lo appendi in camera, l'ospite scansiona e apre il Welcome Book multilingua. Nessuna app da scaricare, nessun login richiesto per l'ospite.",
  },
  {
    icon: Sparkles,
    title: "Dashboard analytics + Codici sconto ospiti fedeli",
    text: "Vedi in tempo reale quante volte è stato aperto il Welcome Book, quali tile sono più cliccate, da quali dispositivi. Genera codici sconto stampabili per ospiti fedeli e traccia gli utilizzi.",
  },
];

const FAQS = [
  {
    q: "Cos'è Hostland e come mi aiuta a risparmiare commissioni Airbnb e Booking?",
    a: "Hostland è la piattaforma italiana per host, property manager, B&B, hotel boutique e affittacamere che vogliono ricevere prenotazioni dirette senza pagare commissioni alle OTA. Ogni tua struttura ottiene un sito web pubblico indicizzabile su Google + un Welcome Book digitale multilingua per gli ospiti. Le prenotazioni arrivano tramite il tuo sito con Stripe integrato: nessuna commissione del 15-18% come su Airbnb o Booking.com.",
  },
  {
    q: "Come funziona il mese gratuito? Serve la carta di credito?",
    a: "No, non serve alcuna carta di credito per iniziare. Ti registri con email e password, hai 30 giorni pieni per pubblicare la struttura, configurare il Welcome Book e ricevere prenotazioni. Solo se decidi di continuare inserisci la carta al 30° giorno. Puoi cancellare quando vuoi con un click dalla dashboard.",
  },
  {
    q: "Quanto costa Hostland dopo il periodo di prova?",
    a: "Il piano Annuale costa €59.90 all'anno (equivalente a €4.99 al mese) e include tutto: sito web SEO, welcome book digitale, prenotazioni dirette, concierge AI, iCal sync, recensioni Google. Il piano Mensile è €9.90 al mese senza vincoli. Chi ha già un sito può scegliere Solo Welcome Book a €35.90 all'anno. C'è anche ParkHost a €4.99 all'anno per chi affitta parcheggi.",
  },
  {
    q: "Il Welcome Book digitale sostituisce davvero il raccoglitore di carta?",
    a: "Sì. Il Welcome Book Hostland include Wi-Fi con password copiabile in un tap (fino a 5 reti per strutture grandi), manuali della casa, mappa POI con ristoranti/bar/supermercati vicini, meteo con alert estremi, eventi in zona, trasporti pubblici, self check-in, concierge AI multilingua e QR code stampabile. È in italiano, inglese, francese, spagnolo e tedesco con traduzioni automatiche.",
  },
  {
    q: "Serve conoscere HTML, CSS o programmazione per usare Hostland?",
    a: "Assolutamente no. La dashboard è progettata per host non tecnici: compili un modulo con nome struttura, foto, indirizzo, prezzo e Hostland genera automaticamente sito web, Welcome Book, QR code e schema SEO. In 15 minuti sei online con una landing indicizzabile su Google.",
  },
  {
    q: "Il CIN (Codice Identificativo Nazionale) è supportato?",
    a: "Sì. Hostland pubblica automaticamente il CIN nella landing page di ogni struttura, come richiesto dalla normativa italiana in vigore dal 2024. Puoi anche mostrare il CIR regionale dove applicabile. Le strutture senza CIN non possono essere pubblicate — garantendo conformità al 100%.",
  },
  {
    q: "Posso sincronizzare il calendario con Airbnb, Booking e VRBO?",
    a: "Sì. Ogni struttura ha un calendario iCal bidirezionale che si sincronizza con Airbnb, Booking.com, VRBO e qualsiasi altra piattaforma che supporti iCal. Le disponibilità restano allineate in tempo reale, senza overbooking.",
  },
  {
    q: "Come funziona il concierge AI e il chatbot per ospiti?",
    a: "Il concierge AI usa GPT-5 e Claude per rispondere alle domande degli ospiti 24/7 in 5 lingue: check-in, Wi-Fi, ristoranti consigliati, orari trasporti, cosa fare in zona. Include l'AI Tour della città generato in automatico dai dati della struttura. Zero configurazione — funziona out-of-the-box.",
  },
  {
    q: "Le recensioni di Google Business vengono importate?",
    a: "Sì. Se colleghi il tuo Google Business Profile, Hostland importa e aggiorna automaticamente le recensioni ogni 7 giorni. Le mostra sulla landing pubblica come social proof, con la vera rating e i commenti reali degli ospiti.",
  },
  {
    q: "Hostland funziona per hotel, boutique hotel e strutture con più camere?",
    a: "Sì. Puoi pubblicare unlimited proprietà con un unico abbonamento. Ogni camera/appartamento ha il suo Welcome Book brandizzato, il suo QR code e la sua landing SEO. Ideale per B&B con 3-10 camere, boutique hotel, case vacanza multiple, agriturismi, residence e gruppi di property management.",
  },
  {
    q: "Posso avere un dominio personalizzato (es. mio-bnb.it)?",
    a: "Sì, con il piano Annuale puoi collegare il tuo dominio custom via Cloudflare for SaaS. La tua landing diventa `www.tuobnb.it` invece di `hostland.vitadahost.it/property/xxx`. SEO transfer completo, certificato SSL automatico.",
  },
  {
    q: "Che differenza c'è tra Hostland e Airbnb / Booking?",
    a: "Airbnb e Booking sono marketplace: prendono 15-18% di commissione su ogni prenotazione e nascondono i tuoi contatti agli ospiti. Hostland è un tuo sito web indipendente: nessuna commissione, contatto diretto con l'ospite, brand tuo, SEO tuo. Puoi comunque restare su Airbnb/Booking in parallelo e usare Hostland per catturare le prenotazioni ripetute e i clienti fedeli senza pagare fee.",
  },
];

const LIVE_LINKS = [
  { label: "Vedi una landing property reale", href: "/property/prop_demo_hostland", icon: Building2 },
  { label: "Prova il Welcome Book digitale", href: "/guestbook/prop_demo_hostland?showcase=1", icon: MessageSquareText },
  { label: "Mappa host in Italia", href: "/map", icon: MapPinned },
  { label: "Classifica host", href: "/leaderboard", icon: Award },
  { label: "GuestBook per Hotel", href: "/guestbook-hotel", icon: Building2 },
  { label: "GuestBook per B&B", href: "/guestbook-bnb", icon: Users },
  { label: "Sito web per host", href: "/sito-web-per-host", icon: Globe },
  { label: "Prenotazioni dirette", href: "/prenotazioni-dirette-senza-commissioni", icon: Zap },
];

/* ═════════════════════════════════════════════════════════════════════════ */
/* MAIN COMPONENT                                                             */
/* ═════════════════════════════════════════════════════════════════════════ */
export default function IniziaLanding() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [showLogin, setShowLogin] = useState(false);

  /* ── UTM capture: preserve source across signup so we can attribute the
       Google Ads → conversion funnel from the analytics dashboard. ── */
  const utm = useMemo(() => {
    if (typeof window === "undefined") return "";
    const p = new URLSearchParams(window.location.search);
    return ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"]
      .filter((k) => p.get(k)).map((k) => `${k}=${encodeURIComponent(p.get(k))}`).join("&");
  }, []);

  const goRegister = (planId = "yearly") => {
    const params = new URLSearchParams();
    if (email) params.set("email", email);
    params.set("plan", planId);
    params.set("trial", "1");
    if (utm) utm.split("&").forEach((kv) => { const [k, v] = kv.split("="); params.set(k, decodeURIComponent(v)); });
    navigate(`/login?${params.toString()}&mode=signup`);
  };

  /* ── GA4 conversion event on landing view ── */
  useEffect(() => {
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      window.gtag("event", "landing_view", { landing: "inizia" });
    }
  }, []);

  return (
    <div className="min-h-screen bg-[#0B0B10] text-zinc-100 overflow-x-hidden">
      <SEOHead />

      {/* ═══ TOP NAV ═══ */}
      <header className="sticky top-0 z-40 backdrop-blur-md bg-black/40 border-b border-white/5">
        <div className="max-w-6xl mx-auto px-5 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="nav-home">
            <img src="/hostland-logo-splash.webp" alt="Hostland" className="w-8 h-8 rounded-lg" />
            <span className="font-black text-lg tracking-tight">Hostland</span>
          </Link>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowLogin((v) => !v)}
              className="text-xs sm:text-sm font-bold text-zinc-300 hover:text-white px-3 py-2"
              data-testid="nav-login-toggle"
            >
              Accedi
            </button>
            <button
              onClick={() => goRegister("yearly")}
              className="text-xs sm:text-sm font-black px-4 py-2 rounded-full bg-gradient-to-r from-[#FF385C] to-[#E61E4D] text-white shadow-lg shadow-rose-500/20 hover:shadow-rose-500/40 transition-shadow"
              data-testid="nav-signup"
            >
              Inizia gratis
            </button>
          </div>
        </div>
      </header>

      {showLogin && <InlineLoginCard onClose={() => setShowLogin(false)} />}

      {/* ═══ HERO ═══ */}
      <Hero email={email} setEmail={setEmail} onCta={() => goRegister("yearly")} />

      {/* ═══ TRUST STRIP ═══ */}
      <TrustStrip />

      {/* ═══ LIVE DEMO ═══ */}
      <LiveDemos />

      {/* ═══ PILLARS ═══ */}
      <Pillars />

      {/* ═══ PLANS ═══ */}
      <Plans onSelect={goRegister} />

      {/* ═══ SOCIAL PROOF ═══ */}
      <SocialProof />

      {/* ═══ EXPLORE LINKS ═══ */}
      <ExploreLinks />

      {/* ═══ FAQ ═══ */}
      <FAQ />

      {/* ═══ FINAL CTA ═══ */}
      <FinalCTA onCta={() => goRegister("yearly")} />

      {/* ═══ FOOTER ═══ */}
      <Footer />
    </div>
  );
}

/* ═══════════════════════════ SUB-COMPONENTS ═══════════════════════════════ */

const SEOHead = () => {
  const title = "Hostland Gratis 1 Mese — Sito Web + Welcome Book Digitale per Host senza commissioni";
  const description = "Inizia gratis 30 giorni senza carta di credito. Sito web SEO + Welcome Book digitale + concierge AI + prenotazioni dirette con Stripe. Zero commissioni Airbnb/Booking. Per host, B&B, hotel, affittacamere e affitti brevi in Italia.";
  const url = `${DOMAIN}/inizia`;

  const orgSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Hostland by Vita da Host",
    url: DOMAIN,
    logo: `${DOMAIN}/hostland-logo.png`,
    description: "Piattaforma italiana per host, property manager e affitti brevi: sito web, welcome book digitale, prenotazioni dirette senza commissioni.",
    sameAs: [
      "https://www.vitadahost.it/",
      "https://vitadahost.it/servizi/hostland",
    ],
  };

  const productSchema = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: "Hostland — Sito web + Welcome Book digitale",
    description,
    image: [
      `${DOMAIN}/hostland-social.png`,
      `${DOMAIN}/hostland-logo.png`,
    ],
    brand: { "@type": "Brand", name: "Hostland" },
    offers: [
      { "@type": "Offer", name: "Annuale", price: "59.90", priceCurrency: "EUR",
        availability: "https://schema.org/InStock", url },
      { "@type": "Offer", name: "Mensile", price: "9.90", priceCurrency: "EUR",
        availability: "https://schema.org/InStock", url },
      { "@type": "Offer", name: "Solo Welcome Book", price: "35.90", priceCurrency: "EUR",
        availability: "https://schema.org/InStock", url },
      { "@type": "Offer", name: "ParkHost", price: "4.99", priceCurrency: "EUR",
        availability: "https://schema.org/InStock", url },
    ],
    aggregateRating: {
      "@type": "AggregateRating", ratingValue: "4.9", reviewCount: "156",
    },
  };

  const softwareSchema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "Hostland",
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web, iOS, Android",
    offers: { "@type": "Offer", price: "0", priceCurrency: "EUR" },
    description,
    featureList: [
      "Sito web SEO per affitti brevi",
      "Welcome Book digitale multilingua",
      "Concierge AI per ospiti",
      "Prenotazioni dirette con Stripe",
      "Self check-in con QR code",
      "iCal sync Airbnb Booking VRBO",
      "Chatbot AI multilingua",
      "Dashboard analytics host",
    ],
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={SEO_KEYWORDS} />
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      <link rel="canonical" href={url} />
      <link rel="alternate" hrefLang="it-IT" href={url} />
      <link rel="alternate" hrefLang="x-default" href={url} />

      <meta property="og:type" content="website" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={`${DOMAIN}/hostland-social.png`} />
      <meta property="og:site_name" content="Hostland" />
      <meta property="og:locale" content="it_IT" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={`${DOMAIN}/hostland-social.png`} />

      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(productSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(softwareSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
    </Helmet>
  );
};

const Hero = ({ email, setEmail, onCta }) => (
  <section className="relative pt-14 pb-16 md:pt-24 md:pb-28 overflow-hidden">
    {/* Animated background blobs */}
    <div aria-hidden className="absolute inset-0 pointer-events-none">
      <motion.div
        animate={{ y: [0, -30, 0], x: [0, 20, 0] }}
        transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }}
        className="absolute -top-32 -left-20 w-[520px] h-[520px] rounded-full opacity-40"
        style={{ background: "radial-gradient(circle, rgba(255,56,92,0.6), transparent 65%)" }}
      />
      <motion.div
        animate={{ y: [0, 40, 0], x: [0, -25, 0] }}
        transition={{ duration: 22, repeat: Infinity, ease: "easeInOut" }}
        className="absolute -bottom-40 -right-24 w-[560px] h-[560px] rounded-full opacity-40"
        style={{ background: "radial-gradient(circle, rgba(251,191,36,0.55), transparent 65%)" }}
      />
      <div className="absolute inset-0" style={{
        backgroundImage: "radial-gradient(rgba(255,255,255,0.06) 1px, transparent 1px)",
        backgroundSize: "22px 22px",
      }} />
    </div>

    <div className="relative max-w-6xl mx-auto px-5 grid md:grid-cols-2 gap-10 items-center">
      <div>
        <motion.span
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          className="inline-flex items-center gap-2 text-[11px] font-black uppercase tracking-widest bg-gradient-to-r from-amber-300 to-yellow-400 text-zinc-900 px-3 py-1.5 rounded-full mb-5"
          data-testid="hero-badge"
        >
          <Sparkles className="w-3.5 h-3.5" strokeWidth={2.4} />
          1 MESE GRATIS · ZERO CARTA DI CREDITO
        </motion.span>
        <motion.h1
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 }}
          className="text-4xl sm:text-5xl md:text-6xl font-black tracking-tight leading-[1.02] text-white"
          data-testid="hero-h1"
        >
          Il sito web + <span className="bg-gradient-to-r from-[#FF385C] via-[#F59E0B] to-[#FBBF24] bg-clip-text text-transparent">Welcome Book digitale</span> per host che vogliono <em className="not-italic">zero commissioni</em>.
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.16 }}
          className="mt-5 text-[17px] md:text-lg text-zinc-300 max-w-xl leading-relaxed"
        >
          Prenotazioni dirette, concierge AI multilingua, mappa POI, meteo con alert estremi, eventi in zona, Wi-Fi copiabile, manuali casa, recensioni Google, dominio custom, QR premium, iCal sync. Pubblichi la tua struttura in 15 minuti e smetti di pagare il <strong>15-18% di commissioni</strong> ad Airbnb e Booking.
        </motion.p>

        {/* Inline signup */}
        <motion.form
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.24 }}
          onSubmit={(e) => { e.preventDefault(); onCta(); }}
          className="mt-8 flex flex-col sm:flex-row gap-2 max-w-lg"
        >
          <input
            type="email" required
            value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="La tua email"
            className="flex-1 bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl px-4 py-3.5 text-white placeholder:text-white/50 outline-none focus:border-[#FF385C]/60 transition"
            data-testid="hero-email"
          />
          <motion.button
            whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
            type="submit"
            className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-2xl font-black bg-gradient-to-r from-[#FF385C] to-[#E61E4D] text-white shadow-xl shadow-rose-500/40 whitespace-nowrap"
            data-testid="hero-cta"
          >
            Inizia gratis <ArrowRight className="w-4 h-4" strokeWidth={2.8} />
          </motion.button>
        </motion.form>

        <motion.ul
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.35 }}
          className="mt-6 flex flex-wrap gap-x-5 gap-y-2 text-[13px] text-zinc-300"
        >
          {[
            "30 giorni gratis",
            "Nessuna carta di credito",
            "Cancelli quando vuoi",
            "Setup in 15 minuti",
          ].map((t) => (
            <li key={t} className="inline-flex items-center gap-1.5">
              <CheckCircle2 className="w-4 h-4 text-emerald-400" strokeWidth={2.4} /> {t}
            </li>
          ))}
        </motion.ul>
      </div>

      {/* Phone mockup with live iframe */}
      <motion.div
        initial={{ opacity: 0, scale: 0.9, rotate: -3 }}
        animate={{ opacity: 1, scale: 1, rotate: -3 }}
        transition={{ delay: 0.3, type: "spring", stiffness: 220, damping: 22 }}
        className="hidden md:flex justify-center"
      >
        <div className="relative w-[300px] h-[600px] rounded-[46px] bg-black shadow-2xl shadow-rose-500/25"
          style={{ padding: "10px", border: "3px solid #27272a" }}>
          <div className="absolute top-3 left-1/2 -translate-x-1/2 w-24 h-5 bg-black rounded-b-2xl z-10" />
          <iframe
            src="/guestbook/prop_demo_hostland?embed=1&showcase=1"
            title="Live Welcome Book demo"
            className="w-full h-full rounded-[36px] bg-white"
            loading="lazy"
          />
        </div>
      </motion.div>
    </div>
  </section>
);

const TrustStrip = () => (
  <section className="border-y border-white/5 bg-black/40">
    <div className="max-w-6xl mx-auto px-5 py-6 flex flex-wrap items-center justify-center gap-x-10 gap-y-3 text-[13px] text-zinc-400">
      <div className="inline-flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-emerald-400" strokeWidth={2.4} /> Made in Italy · GDPR-compliant</div>
      <div className="inline-flex items-center gap-2"><Star className="w-4 h-4 text-amber-400 fill-amber-400" strokeWidth={0} /> 4.9/5 su 156 recensioni host</div>
      <div className="inline-flex items-center gap-2"><Zap className="w-4 h-4 text-yellow-400" strokeWidth={2.4} /> Setup in 15 minuti</div>
      <div className="inline-flex items-center gap-2"><Languages className="w-4 h-4 text-sky-400" strokeWidth={2.4} /> 5 lingue automatiche</div>
    </div>
  </section>
);

const LiveDemos = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });
  return (
    <section ref={ref} className="py-16 md:py-24 relative">
      <div className="max-w-6xl mx-auto px-5">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <span className="text-[11px] font-black uppercase tracking-widest text-amber-300">Prova il prodotto ora</span>
          <h2 className="mt-3 text-3xl md:text-5xl font-black tracking-tight text-white">Demo live interattive, senza registrazione</h2>
          <p className="mt-4 text-zinc-400 text-lg">Ecco esattamente cosa vedrà il tuo ospite quando scansiona il QR in camera — sito web e Welcome Book digitale funzionanti al 100%.</p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {[
            {
              title: "Landing page pubblica",
              subtitle: "Cosa vedono i tuoi ospiti quando cercano su Google — prenotazioni dirette, foto, recensioni, mappa, CIN, prezzi.",
              src: "/property/prop_demo_hostland?embed=1",
              href: "/property/prop_demo_hostland",
              tid: "demo-property",
              badge: "SITO WEB",
            },
            {
              title: "Welcome Book digitale",
              subtitle: "L'esperienza in camera: Wi-Fi tap-to-copy, concierge AI, mappa POI, meteo, eventi, self check-in, manuali, trasporti locali.",
              src: "/guestbook/prop_demo_hostland?embed=1&showcase=1",
              href: "/guestbook/prop_demo_hostland?showcase=1",
              tid: "demo-guestbook",
              badge: "WELCOME BOOK",
            },
          ].map((d, i) => (
            <motion.a
              key={d.tid}
              href={d.href} target="_blank" rel="noopener noreferrer"
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: i * 0.15, type: "spring", stiffness: 200, damping: 24 }}
              whileHover={{ y: -6 }}
              className="group relative block rounded-3xl overflow-hidden bg-gradient-to-br from-zinc-900 to-zinc-950 border border-white/10 shadow-2xl"
              data-testid={d.tid}
            >
              <div className="aspect-[4/5] w-full relative">
                <iframe src={d.src} title={d.title} className="w-full h-full bg-white" loading="lazy" />
                <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/85 to-transparent pointer-events-none" />
                <span className="absolute top-4 left-4 text-[10px] font-black tracking-widest bg-amber-300 text-zinc-900 px-2.5 py-1 rounded-full shadow-lg">
                  {d.badge}
                </span>
                <div className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/95 flex items-center justify-center shadow-lg opacity-0 group-hover:opacity-100 transition-opacity">
                  <PlayCircle className="w-5 h-5 text-zinc-900" strokeWidth={2.4} />
                </div>
              </div>
              <div className="p-5">
                <h3 className="text-lg font-black text-white">{d.title}</h3>
                <p className="text-sm text-zinc-400">{d.subtitle}</p>
                <p className="mt-3 text-xs font-black text-amber-300 group-hover:text-amber-200 inline-flex items-center gap-1">
                  Apri in nuova scheda <ArrowRight className="w-3 h-3" strokeWidth={2.8} />
                </p>
              </div>
            </motion.a>
          ))}
        </div>

        {/* Discover full site CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-10 text-center"
        >
          <p className="text-sm text-zinc-400 mb-4">Vuoi vedere tutto quello che Hostland può fare?</p>
          <a
            href="/"
            className="inline-flex items-center gap-2 px-6 py-3.5 rounded-2xl bg-white/10 hover:bg-white/15 border border-white/20 hover:border-white/30 text-white font-black text-sm transition-all"
            data-testid="cta-main-site"
          >
            <Sparkles className="w-4 h-4 text-amber-300" strokeWidth={2.4} />
            Scopri il sito completo di Hostland
            <ArrowRight className="w-4 h-4" strokeWidth={2.8} />
          </a>
        </motion.div>
      </div>
    </section>
  );
};

const Pillars = () => (
  <section className="py-16 md:py-24 bg-gradient-to-b from-transparent to-black/30">
    <div className="max-w-6xl mx-auto px-5">
      <div className="text-center max-w-2xl mx-auto mb-14">
        <span className="text-[11px] font-black uppercase tracking-widest text-[#FF385C]">Tutto in una piattaforma</span>
        <h2 className="mt-3 text-3xl md:text-5xl font-black tracking-tight text-white">Sostituisci 5 tool con 1 solo abbonamento</h2>
      </div>
      <div className="grid md:grid-cols-2 gap-5">
        {PILLARS.map((p, i) => (
          <motion.article
            key={p.title}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ delay: i * 0.08 }}
            className="rounded-3xl p-6 md:p-8 bg-white/[0.04] backdrop-blur-sm border border-white/10 hover:border-[#FF385C]/40 transition-colors"
            data-testid={`pillar-${i}`}
          >
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#FF385C] to-[#E61E4D] flex items-center justify-center shadow-lg shadow-rose-500/30">
              <p.icon className="w-6 h-6 text-white" strokeWidth={2.2} />
            </div>
            <h3 className="mt-4 text-xl md:text-2xl font-black text-white tracking-tight">{p.title}</h3>
            <p className="mt-2 text-zinc-400 leading-relaxed">{p.text}</p>
          </motion.article>
        ))}
      </div>
    </div>
  </section>
);

const Plans = ({ onSelect }) => (
  <section id="piani" className="py-16 md:py-24">
    <div className="max-w-6xl mx-auto px-5">
      <div className="text-center max-w-2xl mx-auto mb-14">
        <span className="text-[11px] font-black uppercase tracking-widest text-amber-300">Piani flessibili</span>
        <h2 className="mt-3 text-3xl md:text-5xl font-black tracking-tight text-white">Un piano per ogni tipo di host</h2>
        <p className="mt-4 text-zinc-400 text-lg">Inizia con 30 giorni gratis. Nessuna carta di credito richiesta.</p>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        {PLANS.map((plan, i) => (
          <motion.div
            key={plan.id}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ delay: i * 0.08 }}
            className={`relative rounded-3xl p-6 ${plan.highlight
              ? "bg-gradient-to-br from-[#FF385C] to-[#E61E4D] text-white shadow-2xl shadow-rose-500/30 scale-100 md:scale-105 z-10"
              : "bg-white/[0.04] backdrop-blur-sm border border-white/10"}`}
            data-testid={`plan-${plan.id}`}
          >
            {plan.highlight && (
              <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-amber-300 text-zinc-900 text-[10px] font-black uppercase tracking-widest px-3 py-1 rounded-full whitespace-nowrap">
                Più scelto
              </span>
            )}
            <h3 className={`text-xl font-black ${plan.highlight ? "text-white" : "text-white"}`}>{plan.name}</h3>
            <p className={`text-xs font-bold ${plan.highlight ? "text-white/85" : "text-zinc-400"}`}>{plan.tagline}</p>
            <div className="mt-4 flex items-baseline gap-1">
              <span className={`text-4xl font-black ${plan.highlight ? "text-white" : "text-white"}`}>€{plan.price.toFixed(2)}</span>
              <span className={`text-sm font-bold ${plan.highlight ? "text-white/85" : "text-zinc-400"}`}>{plan.periodLabel}</span>
            </div>
            {plan.monthlyEq && (
              <p className={`text-xs mt-1 ${plan.highlight ? "text-white/80" : "text-zinc-500"}`}>{plan.monthlyEq}</p>
            )}
            {plan.save && (
              <p className="inline-block mt-2 text-[10px] font-black bg-amber-300 text-zinc-900 px-2 py-0.5 rounded-full">
                {plan.save}
              </p>
            )}
            <p className={`mt-3 text-xs font-bold ${plan.highlight ? "text-white/90" : "text-emerald-400"}`}>{plan.trial}</p>

            <ul className={`mt-5 space-y-2 text-sm ${plan.highlight ? "text-white/95" : "text-zinc-300"}`}>
              {plan.features.map((f) => (
                <li key={f} className="flex items-start gap-2">
                  <CheckCircle2 className={`w-4 h-4 mt-0.5 shrink-0 ${plan.highlight ? "text-white" : "text-emerald-400"}`} strokeWidth={2.4} />
                  {f}
                </li>
              ))}
            </ul>

            <motion.button
              whileTap={{ scale: 0.97 }}
              onClick={() => onSelect(plan.id)}
              className={`mt-6 w-full py-3.5 rounded-2xl font-black text-sm transition-all ${
                plan.highlight
                  ? "bg-white text-[#E61E4D] hover:bg-zinc-100"
                  : "bg-white/10 hover:bg-white/20 text-white border border-white/20"
              }`}
              data-testid={`plan-cta-${plan.id}`}
            >
              {plan.cta}
            </motion.button>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

const SocialProof = () => {
  const [count, setCount] = useState({ hosts: 0, properties: 0 });
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/users/hosts/count`);
        const j = await r.json();
        setCount({ hosts: j?.count || 0, properties: j?.properties || 0 });
      } catch { /* keep zero, page still works */ }
    })();
  }, []);

  const stats = [
    { label: "Host attivi", value: count.hosts || "50+", icon: Users },
    { label: "Strutture pubblicate", value: count.properties || "120+", icon: Building2 },
    { label: "Lingue supportate", value: "5", icon: Languages },
    { label: "Commissioni pagate", value: "€0", icon: TrendingUp },
  ];

  return (
    <section className="py-16 md:py-20 bg-black/30">
      <div className="max-w-6xl mx-auto px-5">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {stats.map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08 }}
              className="rounded-3xl bg-white/[0.04] border border-white/10 p-5 text-center"
              data-testid={`stat-${i}`}
            >
              <s.icon className="w-6 h-6 mx-auto text-amber-300 mb-2" strokeWidth={2.2} />
              <p className="text-3xl md:text-4xl font-black text-white">{s.value}</p>
              <p className="text-xs text-zinc-400 font-bold uppercase tracking-wide mt-1">{s.label}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

const ExploreLinks = () => (
  <section className="py-16 md:py-20">
    <div className="max-w-6xl mx-auto px-5">
      <div className="text-center max-w-2xl mx-auto mb-10">
        <span className="text-[11px] font-black uppercase tracking-widest text-sky-300">Approfondisci</span>
        <h2 className="mt-3 text-2xl md:text-4xl font-black text-white tracking-tight">Vedi Hostland in azione</h2>
      </div>
      <nav className="grid grid-cols-2 md:grid-cols-4 gap-3" aria-label="Approfondimenti Hostland">
        {LIVE_LINKS.map((l) => (
          <a
            key={l.href}
            href={l.href}
            className="group flex items-center gap-3 p-4 rounded-2xl bg-white/[0.04] border border-white/10 hover:border-[#FF385C]/40 hover:bg-white/[0.07] transition-colors"
            data-testid={`explore-${l.href}`}
          >
            <l.icon className="w-5 h-5 text-amber-300 shrink-0" strokeWidth={2.2} />
            <span className="text-sm font-bold text-zinc-200 group-hover:text-white leading-tight">{l.label}</span>
          </a>
        ))}
      </nav>
    </div>
  </section>
);

const FAQ = () => {
  const [open, setOpen] = useState(0);
  return (
    <section className="py-16 md:py-24 bg-gradient-to-b from-transparent to-black/40">
      <div className="max-w-3xl mx-auto px-5">
        <div className="text-center mb-12">
          <span className="text-[11px] font-black uppercase tracking-widest text-emerald-300">Domande frequenti</span>
          <h2 className="mt-3 text-3xl md:text-5xl font-black tracking-tight text-white">Le risposte che cerchi</h2>
        </div>
        <div className="space-y-2">
          {FAQS.map((f, i) => (
            <motion.article
              key={i}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="rounded-2xl bg-white/[0.03] border border-white/10 overflow-hidden"
              data-testid={`faq-${i}`}
            >
              <button
                onClick={() => setOpen(open === i ? -1 : i)}
                className="w-full flex items-center justify-between p-5 text-left"
              >
                <h3 className="text-[15px] md:text-base font-black text-white pr-4">{f.q}</h3>
                <ChevronDown
                  className={`w-5 h-5 text-zinc-400 shrink-0 transition-transform ${open === i ? "rotate-180" : ""}`}
                  strokeWidth={2.4}
                />
              </button>
              {open === i && (
                <motion.p
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  className="px-5 pb-5 text-zinc-300 leading-relaxed text-[14px]"
                >
                  {f.a}
                </motion.p>
              )}
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
};

const FinalCTA = ({ onCta }) => (
  <section className="py-16 md:py-28 relative overflow-hidden">
    <div aria-hidden className="absolute inset-0 pointer-events-none">
      <div className="absolute inset-0 bg-gradient-to-br from-[#FF385C] via-[#E61E4D] to-[#B91C43]" />
      <motion.div
        animate={{ rotate: 360 }}
        transition={{ duration: 60, repeat: Infinity, ease: "linear" }}
        className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full opacity-20"
        style={{ background: "conic-gradient(from 180deg, #FBBF24, transparent, #FBBF24)" }}
      />
    </div>

    <div className="relative max-w-3xl mx-auto px-5 text-center text-white">
      <Rocket className="w-14 h-14 mx-auto mb-5 drop-shadow-lg" strokeWidth={1.6} />
      <h2 className="text-3xl md:text-5xl font-black tracking-tight leading-tight">
        Inizia oggi, il tuo primo mese è offerto da noi.
      </h2>
      <p className="mt-4 text-lg md:text-xl text-white/90">
        Nessuna carta di credito. Cancelli quando vuoi. Setup in 15 minuti.
      </p>
      <motion.button
        whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.97 }}
        onClick={onCta}
        className="mt-8 inline-flex items-center gap-3 px-8 py-4 rounded-2xl bg-white text-[#E61E4D] font-black text-lg shadow-2xl"
        data-testid="final-cta"
      >
        Attiva 30 giorni gratis <ArrowRight className="w-5 h-5" strokeWidth={2.8} />
      </motion.button>
      <p className="mt-4 text-xs text-white/70 font-bold uppercase tracking-widest">
        Made in Italy · Zero commissioni · GDPR
      </p>
    </div>
  </section>
);

const Footer = () => (
  <footer className="border-t border-white/5 bg-black/60 py-10">
    <div className="max-w-6xl mx-auto px-5">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-sm">
        <div>
          <p className="font-black text-white mb-3">Hostland</p>
          <ul className="space-y-2 text-zinc-400">
            <li><Link to="/" className="hover:text-white">Home</Link></li>
            <li><Link to="/login" className="hover:text-white">Accedi</Link></li>
            <li><Link to="/map" className="hover:text-white">Mappa host</Link></li>
            <li><Link to="/leaderboard" className="hover:text-white">Classifica</Link></li>
          </ul>
        </div>
        <div>
          <p className="font-black text-white mb-3">Prodotti</p>
          <ul className="space-y-2 text-zinc-400">
            <li><Link to="/guestbook-digitale" className="hover:text-white">GuestBook digitale</Link></li>
            <li><Link to="/sito-web-per-host" className="hover:text-white">Sito web per host</Link></li>
            <li><Link to="/prenotazioni-dirette-senza-commissioni" className="hover:text-white">Prenotazioni dirette</Link></li>
            <li><Link to="/welcome-book-digitale" className="hover:text-white">Welcome Book</Link></li>
          </ul>
        </div>
        <div>
          <p className="font-black text-white mb-3">Per chi</p>
          <ul className="space-y-2 text-zinc-400">
            <li><Link to="/guestbook-hotel" className="hover:text-white">Hotel</Link></li>
            <li><Link to="/guestbook-bnb" className="hover:text-white">B&B</Link></li>
            <li><Link to="/guestbook-affitti-brevi" className="hover:text-white">Affitti brevi</Link></li>
            <li><Link to="/guestbook-agriturismo" className="hover:text-white">Agriturismi</Link></li>
          </ul>
        </div>
        <div>
          <p className="font-black text-white mb-3">Legale</p>
          <ul className="space-y-2 text-zinc-400">
            <li><Link to="/privacy" className="hover:text-white">Privacy</Link></li>
            <li><Link to="/terms" className="hover:text-white">Termini</Link></li>
            <li><Link to="/cookie-policy" className="hover:text-white">Cookie</Link></li>
            <li><a href="https://vitadahost.it/servizi/hostland" className="hover:text-white">Vita da Host</a></li>
          </ul>
        </div>
      </div>
      <div className="mt-8 pt-6 border-t border-white/5 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-zinc-500">
        <p>© 2026 Hostland by Vita da Host · P.IVA 02903680995</p>
        <p>Made in Italy · Server GDPR-compliant in EU</p>
      </div>
    </div>
  </footer>
);

const InlineLoginCard = ({ onClose }) => {
  const navigate = useNavigate();
  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
      className="fixed top-14 right-4 z-50 w-80 bg-zinc-900 border border-white/10 rounded-2xl shadow-2xl p-5"
      data-testid="inline-login"
    >
      <p className="text-sm font-black text-white mb-3">Hai già un account?</p>
      <button
        onClick={() => navigate("/login")}
        className="w-full py-3 rounded-xl bg-white text-zinc-900 font-black text-sm"
      >
        Vai al login →
      </button>
      <p className="text-xs text-zinc-500 mt-3">Non hai un account? Chiudi e usa il form in alto.</p>
      <button onClick={onClose} className="absolute top-2 right-2 text-zinc-400 hover:text-white text-lg leading-none p-1">×</button>
    </motion.div>
  );
};
